'use client'

import React, { createContext, useContext, useId } from 'react'

import { chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import {
  radioGroupStyles,
  radioInputStyles,
  radioLabelStyles,
  radioRootStyles,
} from './Radio.styles'

type RadioGroupContextValue = {
  name: string
  value: string | null
  onChange: (value: string) => void
  colorScheme?: ColorScheme
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null)

export type RadioGroupProps = {
  name: string
  value: string | null
  onChange: (value: string) => void
  children: React.ReactNode
  colorScheme?: ColorScheme
  direction?: 'column' | 'row'
  /** Accessible name for the radiogroup (the question the options answer). */
  'aria-label'?: string
  /** ID of an existing visible heading that names the radiogroup. */
  'aria-labelledby'?: string
}

export const RadioGroup = ({
  name,
  value,
  onChange,
  children,
  colorScheme,
  direction = 'column',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
}: RadioGroupProps) => (
  <RadioGroupContext.Provider value={{ name, value, onChange, colorScheme }}>
    <chakra.div
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      role='radiogroup'
      {...radioGroupStyles(direction)}
    >
      {children}
    </chakra.div>
  </RadioGroupContext.Provider>
)

export type RadioProps = {
  label: React.ReactNode
  value: string
  isDisabled?: boolean
  colorScheme?: ColorScheme
  /** Standalone use (no RadioGroup ancestor): controlled checked state. */
  isChecked?: boolean
  /** Standalone use: called with this radio's value. Inside a RadioGroup the
   * group's onChange is used instead. */
  onChange?: (value: string) => void
  /**
   * Names the radio for assistive technology when the visible `label` is empty
   * (e.g. a selection column whose meaning comes from the row beside it).
   */
  'aria-label'?: string
}

export const Radio = ({
  label,
  value,
  isDisabled = false,
  colorScheme,
  isChecked,
  onChange,
  'aria-label': ariaLabel,
}: RadioProps) => {
  const group = useContext(RadioGroupContext)
  const inputId = useId()

  const resolvedColorScheme = colorScheme ?? group?.colorScheme ?? 'dark'
  const isRadioChecked = group ? group.value === value : isChecked ?? false

  const handleChange = () => {
    if (isDisabled) return
    if (group) {
      group.onChange(value)
    } else {
      onChange?.(value)
    }
  }

  return (
    <chakra.label htmlFor={inputId} {...radioRootStyles(isDisabled)}>
      <chakra.input
        aria-label={ariaLabel}
        checked={isRadioChecked}
        disabled={isDisabled}
        id={inputId}
        name={group?.name}
        onChange={handleChange}
        type='radio'
        value={value}
        {...radioInputStyles(resolvedColorScheme, isDisabled)}
      />
      <chakra.span {...radioLabelStyles(resolvedColorScheme, isDisabled)}>
        {label}
      </chakra.span>
    </chakra.label>
  )
}
